import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { ticketParticipantsSet, ticketStagesSet } from '~/actions/ticket';
import { getTicketParticipants, patchTicketApproval } from '~/api/tickets';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TicketStatusType } from '~/enums/TicketStatusType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useAsync } from '~/hooks/useAsync';
import { api } from '~/redux/api/createApi';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';
import * as enums from '~/ui/enums/input';
import { useModal } from '~/ui/hooks/modal';

import { useActivityLogUpdater } from '../../shared/useActivityLogUpdater';

function ApprovalItemApproveReject({
  ticketId,
  approvalId,
  approvalName,
  disabled = false,
}) {
  const [note, setNote] = useState('');
  const [status, setStatus] = useState('');
  const useTicketEmailActionWithComment = useFlag(
    FlagType.TicketEmailActionWithComment,
  );
  const dispatch = useDispatch();
  const intl = useIntl();
  const updateActivityLog = useActivityLogUpdater({ ticketId });
  const [getTicketCurrentStage] =
    api.endpoints.getTicketCurrentStage.useLazyQuery();

  useEffect(() => {
    if (status) {
      if (status === TicketStatusType.Rejected) showModal();
      else if (useTicketEmailActionWithComment) showModal();
      else patchTicketApprovalReject();
    }
  }, [status]);

  function handleApproveRejectItem(status) {
    setStatus(status);
  }

  let noteToDisplay;
  if (useTicketEmailActionWithComment || status === TicketStatusType.Rejected) {
    noteToDisplay = note;
  } else {
    noteToDisplay = null;
  }

  const { executor: patchTicketApprovalReject, isLoading } = useAsync(
    patchTicketApproval,
    {
      ticketId,
      status,
      approvalId,
      note: noteToDisplay,
    },
    {
      errorToastMessage: intl.formatMessage({
        id: 'ApprovalItemApproveReject.updateFailed',
        defaultMessage:
          'Updating judgment result failed. Please refresh the page and try again.',
        description: 'Error toast message when updating judgment result fails.',
      }),
      errorHandler: () => {
        setStatus('');
      },
      successHandler: async () => {
        const res = await getTicketCurrentStage({ ticketId }).unwrap();
        dispatch(ticketStagesSet(res));
        await updateActivityLog();
        const participants = await getTicketParticipants({ ticketId });
        dispatch(ticketParticipantsSet(participants.data));
        setStatus('');
      },
    },
  );

  let titleToDisplay;
  const rejectTitle = intl.formatMessage({
    id: 'ApprovalItemApproveReject.rejectTitle',
    defaultMessage: 'Reject the Item?',
    description: 'Modal title to confirm rejection of an item.',
  });
  const approveTitle = intl.formatMessage({
    id: 'ApprovalItemApproveReject.approveTitle',
    defaultMessage: 'Approve the Item?',
    description: 'Modal title to confirm approval of an item.',
  });

  if (useTicketEmailActionWithComment) {
    titleToDisplay =
      status === TicketStatusType.Rejected ? rejectTitle : approveTitle;
  } else {
    titleToDisplay = rejectTitle;
  }

  let isDisabled;
  if (useTicketEmailActionWithComment) {
    isDisabled =
      status === TicketStatusType.Rejected &&
      (!note || note.length > enums.CHARACTER_INPUT_LIMIT_LONG);
  } else {
    isDisabled = !note || note.length > enums.CHARACTER_INPUT_LIMIT_LONG;
  }

  let descriptionToDisplay;
  const rejectDescription = intl.formatMessage({
    id: 'ApprovalItemApproveReject.rejectDescription',
    defaultMessage: 'Leave a comment outlining the reason for your rejection.',
    description: 'Description for the note field when rejecting an item.',
  });
  const approveDescription = intl.formatMessage({
    id: 'ApprovalItemApproveReject.approveDescription',
    defaultMessage: 'Leave a comment outlining the reason for your approval.',
    description: 'Description for the note field when approving an item.',
  });

  if (useTicketEmailActionWithComment) {
    descriptionToDisplay =
      status === TicketStatusType.Rejected
        ? rejectDescription
        : approveDescription;
  } else {
    descriptionToDisplay = rejectDescription;
  }

  const [modal, showModal] = useModal({
    title: titleToDisplay,
    actionButton: {
      disabled: isDisabled,
      text: intl.formatMessage({
        id: 'ApprovalItemApproveReject.confirmButton',
        defaultMessage: 'Confirm',
        description: 'Confirm button text in the modal.',
      }),
      promise: () => patchTicketApprovalReject(),
    },
    content: (
      <FlexLayout flexDirection="column" space={8}>
        <FlexLayout flexDirection="column" space={2}>
          <Text variant="xs-dense">
            {status === TicketStatusType.Rejected ? (
              <FormattedMessage
                id="ApprovalItemApproveReject.rejectConfirmationMessage"
                defaultMessage="Are you sure you want to reject the item {approvalName}?"
                description="Confirmation message in the modal to reject an item. The approvalName is a variable for the item's name. Example: Are you sure you want to reject the item 'Legal Review'?"
                values={{
                  approvalName: `"${approvalName}"`,
                }}
              />
            ) : (
              <FormattedMessage
                id="ApprovalItemApproveReject.approveConfirmationMessage"
                defaultMessage="Are you sure you want to approve the item {approvalName}?"
                description="Confirmation message in the modal to approve an item. The approvalName is a variable for the item's name. Example: Are you sure you want to approve the item 'Legal Review'?"
                values={{
                  approvalName: `"${approvalName}"`,
                }}
              />
            )}
          </Text>
        </FlexLayout>
        <FormField
          label={intl.formatMessage({
            id: 'ApprovalItemApproveReject.noteLabel',
            defaultMessage: 'Note',
            description: 'Label for the note input field.',
          })}
          description={descriptionToDisplay}
          input={TextArea}
          value={note}
          inputProps={{
            onChange: (value) => setNote(value),
            placeholder: intl.formatMessage({
              id: 'ApprovalItemApproveReject.notePlaceholder',
              defaultMessage: 'Add a comment…',
              description: 'Placeholder text for the note input field.',
            }),
            rows: 5,
          }}
        />
      </FlexLayout>
    ),
    onCancel: () => setStatus(''),
  });

  return (
    <FlexLayout alignItems="center" space={4}>
      {modal}
      <IconButton
        icon="check"
        tooltip={intl.formatMessage({
          id: 'ApprovalItemApproveReject.approveTooltip',
          defaultMessage: 'Approve',
          description: 'Tooltip for the approve button.',
        })}
        isLoading={isLoading}
        status="success"
        disabled={disabled}
        onClick={() => handleApproveRejectItem(TicketStatusType.Approved)}
      />
      <IconButton
        icon="x"
        tooltip={intl.formatMessage({
          id: 'ApprovalItemApproveReject.rejectTooltip',
          defaultMessage: 'Reject',
          description: 'Tooltip for the reject button.',
        })}
        status="danger"
        isLoading={isLoading}
        disabled={disabled}
        onClick={() => handleApproveRejectItem(TicketStatusType.Rejected)}
      />
    </FlexLayout>
  );
}
export default ApprovalItemApproveReject;
